/**
 * `useSlotNames` — the slot id → display name (callsign / "GM") resolver. Every surface that
 * turns a server-stamped slot id into human-readable text shares ONE resolution rather than
 * growing a second `Map`. Resolved **client-side** off the live slot presence
 * (`useGameSessionContext().slots`) — the wire itself never carries a name, only the
 * server-stamped slot id.
 *
 * `""` resolves to `"OMNINET"` — the system/feed author sentinel chat's system lines and the
 * activity feed post under. Any OTHER slot id not (yet) present in `slots` — a name that hasn't
 * arrived yet, or a genuinely stale/unknown id — resolves to the literal `"UNKNOWN"`, matching
 * `OMNINET`'s all-caps terminal-chrome sentinel style. **Never the raw slot id**: a raw slot id
 * is a slug of the display name (`gm`/`pilot-1` — see `slotAdmin.ts`'s `mintSlotId`), and
 * leaking one into user-facing text is exactly what this hook exists to prevent.
 */
import { useMemo } from "react";
import { useGameSessionContext } from "./GameSessionProvider";

/**
 * The pure resolution rule, split out from {@link useSlotNames} so it's unit-testable without a
 * React tree or a live `GameSessionProvider`. `nameById` is a plain slot-id → name map (what the
 * hook below builds from `slots` each render).
 */
export function resolveSlotName(nameById: ReadonlyMap<string, string>, slotId: string): string {
  if (slotId === "") return "OMNINET";
  return nameById.get(slotId) ?? "UNKNOWN";
}

export function useSlotNames(): (slotId: string) => string {
  const { slots } = useGameSessionContext();
  return useMemo(() => {
    const map = new Map(slots.map((s) => [s.id, s.name]));
    return (slotId: string): string => resolveSlotName(map, slotId);
  }, [slots]);
}
